<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { watchDebounced } from '@vueuse/core'
import { VIcon, VImageUploader, VTextField } from '@/modules/shared/components'
import GroupUrlBanner from '@/modules/workspace/components/group-url-banner.vue'
import { useWorkspaceOnboardingStore } from '../../stores/workspace-onboarding-store'
import { useWorkspaceSettingsStore } from '../../stores/workspace-settings-store'

const onboardingStore = useWorkspaceOnboardingStore()
const settingsStore = useWorkspaceSettingsStore()

const form = ref({ name: '', phone: '', website: '' })
const nameError = ref('')
const nameChanged = ref(false)

// Track when user actually changes the name
watch(
  () => form.value.name,
  (newName, oldName) => {
    if (oldName !== '' && newName !== oldName) {
      nameChanged.value = true
    }
  },
)

// Generate live slug from firm name
const liveSlug = computed(() => {
  const name = form.value.name.trim()
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .slice(0, 30)
})

// Show current subdomain if name hasn't changed, otherwise show live slug
const displaySlugOverride = computed(() => {
  if (!nameChanged.value) return onboardingStore.workspace?.subdomain || liveSlug.value
  return null
})

// Subdomain availability
const subdomainStatus = ref<'idle' | 'checking' | 'available' | 'taken'>('idle')
const subdomainSuggestion = ref<string | null>(null)
const validatedSubdomain = ref<string>('')

// Debounced check when slug changes — only after user changes the name
watchDebounced(
  liveSlug,
  async (slug) => {
    if (!nameChanged.value) return

    if (!slug) {
      subdomainStatus.value = 'idle'
      subdomainSuggestion.value = null
      validatedSubdomain.value = ''
      return
    }

    // Skip check if slug matches current group's subdomain
    const currentSubdomain = onboardingStore.workspace?.subdomain
    if (slug === currentSubdomain) {
      subdomainStatus.value = 'available'
      validatedSubdomain.value = slug
      subdomainSuggestion.value = null
      return
    }

    subdomainStatus.value = 'checking'
    const result = await settingsStore.checkSubdomain(slug)

    if (result.available) {
      subdomainStatus.value = 'available'
      validatedSubdomain.value = slug
      subdomainSuggestion.value = null
    } else {
      subdomainStatus.value = 'taken'
      subdomainSuggestion.value = result.suggestion || null
      validatedSubdomain.value = result.suggestion || slug
    }
  },
  { debounce: 500 },
)

const useSuggestion = () => {
  if (subdomainSuggestion.value) {
    validatedSubdomain.value = subdomainSuggestion.value
    subdomainStatus.value = 'available'
    subdomainSuggestion.value = null
  }
}

const displaySlug = computed(() => {
  if (subdomainStatus.value === 'taken' && subdomainSuggestion.value) {
    return subdomainSuggestion.value
  }
  return liveSlug.value
})

const logoPreview = ref<string | null>(null)
const logoFile = ref<File | null>(null)
const replaceLogo = ref(false)

const onLogoUpload = (file: File) => {
  logoFile.value = file
  replaceLogo.value = true
}

const onLogoRemove = () => {
  logoPreview.value = null
  logoFile.value = null
  replaceLogo.value = true
}

watch(
  () => onboardingStore.workspace,
  (ws) => {
    if (!ws) return
    form.value.name = ws.name ?? ''
    form.value.phone = ws.phone ?? ''
    form.value.website = ws.website ?? ''
    logoPreview.value = ws.logo_url ?? null
  },
  { immediate: true },
)

const isChecking = computed(
  () => !form.value.name.trim() || subdomainStatus.value === 'checking' || subdomainStatus.value === 'taken',
)

const submit = async (): Promise<boolean> => {
  nameError.value = ''
  if (!form.value.name.trim()) {
    nameError.value = 'Firm name is required'
    return false
  }

  if (isChecking.value) {
    nameError.value = 'Please wait while we check subdomain availability'
    return false
  }

  if (!validatedSubdomain.value && liveSlug.value) {
    validatedSubdomain.value = liveSlug.value
  }

  const payload: Record<string, unknown> = {
    name: form.value.name,
    phone: form.value.phone,
    website: form.value.website,
  }

  // Only send subdomain if it changed from the current one
  const currentSubdomain = onboardingStore.workspace?.subdomain
  if (validatedSubdomain.value && validatedSubdomain.value !== currentSubdomain) {
    payload.subdomain = validatedSubdomain.value
  }

  if (logoFile.value) payload.logo = logoFile.value
  if (replaceLogo.value && !logoFile.value) payload.remove_logo = true

  try {
    await settingsStore.updateWorkspace(payload, { silent: true })

    // If subdomain changed, navigate to new URL so route params update
    if (validatedSubdomain.value && validatedSubdomain.value !== currentSubdomain) {
      window.location.href = `/${validatedSubdomain.value}/workspace/onboarding`
      return false // Prevent advance_step — page will reload with new slug
    }

    await onboardingStore.fetch()
    return true
  } catch {
    nameError.value = 'Failed to save. Please try again.'
    return false
  }
}

defineExpose({ submit, isChecking })
</script>

<template>
  <div class="flex flex-1 flex-col">
    <!-- Logo Upload -->
    <div class="mb-6 flex justify-center">
      <VImageUploader
        :image="logoPreview"
        accept=".png,.jpg,.jpeg,.svg"
        label="Logo"
        @upload="onLogoUpload"
        @remove="onLogoRemove"
      />
    </div>

    <div class="space-y-4">
      <VTextField
        v-model="form.name"
        label="Firm name"
        name="name"
        placeholder="e.g. Sequoia Capital"
        :error="nameError"
      />

      <div v-if="liveSlug">
        <GroupUrlBanner
          :subdomain="displaySlugOverride || displaySlug"
          description="Place this link on your website. Investors use it to log in to their portal, and companies can create accounts to submit applications and updates."
        />
        <div v-if="nameChanged" class="mt-2 flex items-center gap-2 text-xs">
          <span v-if="subdomainStatus === 'checking'" class="text-gray-400">Checking availability...</span>
          <span v-else-if="subdomainStatus === 'available'" class="text-emerald-600">Available</span>
          <span v-else-if="subdomainStatus === 'taken'" class="text-amber-600">
            Already taken.
            <button
              v-if="subdomainSuggestion"
              @click="useSuggestion"
              class="font-semibold text-[#3D8A9D] hover:underline"
            >
              Use {{ subdomainSuggestion }}.venture360.co instead?
            </button>
          </span>
        </div>
      </div>

      <VTextField
        v-model="form.website"
        label="Firm website"
        name="website"
        type="url"
        placeholder="https://www.yourfirm.com"
      />
      <VTextField v-model="form.phone" label="Contact phone" name="phone" type="tel" placeholder="+1 (555) 000-0000" />
    </div>
  </div>
</template>
